import {
  PaginatedResponse,
  StandardSummaryResponse,
} from '../../models/paginated-response'
import { unifiedGet } from './unifiedApiRequest'

/**
 * Unified opportunity-DOORS API methods (FIR-6995).
 *
 * The door mirror of `unifiedOpportunitiesApi.ts`. Both endpoints take a backend
 * `unified_target_type` (resolve app vertical names through `getTargetType` in
 * `targetTypeMap.ts` — never pass a raw vertical slug here). Only the three
 * door-bearing verticals are valid: `restaurant`, `lodging`, `cstore`.
 *
 * The list returns the standard paginated shape; each row carries the shared
 * door columns (`name`/`domain`, COALESCEd over CRM overrides, plus
 * `street_address`/`city`/`state`/`zipcode`/`country`/`phone`, `distributors`,
 * `gpos`, `taglist`, `note_count`) FLAT at the top level, plus that vertical's
 * door segment columns under their legacy names (`place_url`, `reviews_avg`,
 * `chain_doors_cnt`, restaurant `chain_name`/`avg_rating`/…). The ids on the row
 * are `company_id` (chain-proxy id), `door_id` (door-proxy id), and `detail_id`
 * (the per-vertical GooglePlace/restaurant door id used for the open-detail
 * link). Run rows through `adaptUnifiedDoorItemForVertical`
 * (`unifiedDoorResponseAdapter.ts`) before feeding the legacy door columns, and
 * outgoing params through `adaptUnifiedListParams`.
 *
 * The summary returns `{ count, alt_count }`: `count` = DOOR count (one row per
 * door) and `alt_count` = distinct parent-chain count
 * (`Count("contact_company_id", distinct=True)`), matching the semantics of the
 * removed per-vertical door summaries so the chain stat card stays correct
 * (see `useUnifiedDoorsTableActions`).
 */

/** A unified door row: shared door columns plus vertical door segment columns. */
export type UnifiedDoorRow = Record<string, unknown>

/**
 * GET `/api/v1/opportunities/doors/?target_type=<tt>&...` — paginated list of
 * doors for one door-bearing vertical (shared + segment columns).
 */
export async function getOpportunityDoorsList(
  targetType: string,
  params: Record<string, unknown> = {},
  signal?: AbortSignal
): Promise<PaginatedResponse<UnifiedDoorRow>> {
  return unifiedGet<PaginatedResponse<UnifiedDoorRow>>(
    '/opportunities/doors/',
    targetType,
    params,
    signal
  )
}

/**
 * The unified doors summary: door `count`, distinct parent-chain `alt_count`,
 * and the (product-aware, filter-scoped) `total_ltv` — the full stat-card set,
 * so the doors card sources everything here. `total_ltv` is restaurant-only
 * (0 otherwise).
 */
export interface UnifiedDoorsSummaryResponse extends StandardSummaryResponse {
  total_ltv: number
}

/**
 * GET `/api/v1/opportunities/doors/summary/?target_type=<tt>&...` — aggregate
 * door `count` + distinct parent-chain `alt_count` + `total_ltv` for the same
 * filtered set, keeping the stat cards in sync with the list.
 */
export async function getOpportunityDoorsSummary(
  targetType: string,
  params: Record<string, unknown> = {},
  signal?: AbortSignal
): Promise<UnifiedDoorsSummaryResponse> {
  return unifiedGet<UnifiedDoorsSummaryResponse>(
    '/opportunities/doors/summary/',
    targetType,
    params,
    signal
  )
}
